/**
 * /compliance layout (US-031.4).
 *
 * Gates all child routes on Member role in ('owner', 'admin', 'compliance')
 * for at least one org. Non-qualifying members are bounced to /dashboard.
 * The gate runs as a server-side `beforeLoad` so a deep link never flashes
 * the compliance UI to an unauthorized user.
 *
 * Sidebar nav lists the compliance pages — currently Verifications +
 * NIL Go submissions; additional compliance surfaces (audit log filtered
 * to this org, etc.) will land here over US-033/US-034.
 *
 * No motion wrapper on the sidebar: framer-motion's `FadeIn` mounts with
 * `initial: { opacity: 0 }` after client hydration, briefly making the
 * sidebar invisible every time the layout (re)mounts during navigation
 * — the "sometimes the sidebar shows, sometimes it doesn't" report from
 * 2026-06-16. SSR + a static <aside> is the right shape for navigation
 * chrome.
 */
import { createFileRoute, Link, Outlet, redirect } from '@tanstack/react-router';

export const Route = createFileRoute('/_authenticated/compliance')({
  beforeLoad: async ({ context }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    const me = await context.queryClient.ensureQueryData(serverApi.user.me.queryOptions());
    if (!me.isOrgStaff) {
      // Staff persona without an org yet → bounce to org creation so the
      // first thing they do is set up their first org. Athletes who
      // wandered here shouldn't see this surface; nav link is hidden for
      // them, so this redirect is mostly defensive.
      if (me.userType === 'staff') {
        throw redirect({ to: '/settings/organizations' });
      }
      throw redirect({ to: '/dashboard' });
    }
  },
  component: ComplianceLayout,
});

function ComplianceLayout() {
  return (
    <div className="flex min-h-screen">
      <aside className="w-56 shrink-0 border-r bg-card p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Compliance
        </p>
        <nav className="flex flex-col gap-1 text-sm">
          <Link
            to="/compliance/verifications"
            className="rounded px-2 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            activeProps={{ className: 'bg-muted text-foreground font-medium' }}
          >
            Verifications
          </Link>
          <Link
            to="/compliance/submissions"
            className="rounded px-2 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            activeProps={{ className: 'bg-muted text-foreground font-medium' }}
          >
            NIL Go submissions
          </Link>
        </nav>
      </aside>
      <main className="flex-1 p-6">
        <Outlet />
      </main>
    </div>
  );
}
